import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useDialog } from '../components/DialogProvider';
import {
  getWorkspaceSummary, getWorkspaceQueue, getDueFollowUps, getMeetings, completeFollowUp,
  getMyTasksPaged, completeTask,
} from '../services/api';
import {
  Box, Typography, Paper, Grid, Stack, Button, CircularProgress, Card, CardActionArea,
  Chip, List, ListItemButton, ListItemText, ListItemIcon, TextField, IconButton,
  Drawer, Divider, Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  FiberNew as NewIcon,
  RateReview as ReviewIcon,
  TrendingUp as HighFitIcon,
  PersonSearch as PersonSearchIcon,
  Send as SendIcon,
  AccessAlarm as AlarmIcon,
  Cancel as CancelIcon,
  Handshake as HandshakeIcon,
  HourglassBottom as HourglassBottomIcon,
  AutoFixHigh as AutoFixHighIcon,
  Update as UpdateIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import AzotteGrid from '../components/AzotteGrid';

const QUICK_ACTIONS = [
  { label: 'Leads', icon: <PeopleIcon />, path: '/leads', desc: 'Browse & manage leads' },
  { label: 'Discover Leads', icon: <SearchIcon />, path: '/ai-search', desc: 'Find new leads with AI', hideOnMobile: true },
  { label: 'Customers', icon: <BusinessIcon />, path: '/customers', desc: 'Manage customer accounts' },
  { label: 'Campaigns', icon: <CampaignIcon />, path: '/engage', desc: 'Outreach & engagement', hideOnMobile: true },
  { label: 'Calendar', icon: <CalendarMonthIcon />, path: '/calendar', desc: 'View meetings & schedule' },
];

const QUEUE_ICON_MAP = {
  'my-tasks': <AssignmentIcon fontSize="small" />,
  'new-discoveries': <NewIcon fontSize="small" />,
  'needs-review': <ReviewIcon fontSize="small" />,
  'high-fit': <HighFitIcon fontSize="small" />,
  'partner-candidates': <HandshakeIcon fontSize="small" />,
  'missing-contacts': <PersonSearchIcon fontSize="small" />,
  'ready-for-outreach': <SendIcon fontSize="small" />,
  'in-outreach-silent': <HourglassBottomIcon fontSize="small" />,
  'enrichment-pending': <AutoFixHighIcon fontSize="small" />,
  'stale-verdict': <UpdateIcon fontSize="small" />,
  'domain-stale': <PublicIcon fontSize="small" />,
  'follow-ups-due': <AlarmIcon fontSize="small" />,
  'rejected': <CancelIcon fontSize="small" />,
};

const QUEUE_DESCRIPTIONS = {
  'my-tasks': 'Tasks assigned to you. Filter by Open, Overdue, or Due today.',
  'new-discoveries': 'Leads added but not yet triaged (Status: New) — review and move forward.',
  'needs-review': 'AI suggested fields or set verdict to Unknown — confirm before action.',
  'high-fit': 'AI verdict = CurrentFit. Strongest ICP match — push to outreach.',
  'partner-candidates': 'Not direct ICP, but can refer or resell to your buyers.',
  'missing-contacts': 'Fit leads with no contacts yet — find decision-makers.',
  'ready-for-outreach': 'Qualified, enriched, domain-checked, with contacts, no active campaign — send.',
  'in-outreach-silent': 'In outreach but no interaction in last 14 days — nudge or re-sequence.',
  'enrichment-pending': 'Auto-enrichment job pending or running — wait or open job.',
  'stale-verdict': 'AI verdict older than 90 days — re-enrich to refresh.',
  'domain-stale': 'Domain never checked or last checked over 30 days ago — recheck health.',
  'follow-ups-due': 'Interactions with a follow-up date due today or earlier.',
  'rejected': 'Disqualified leads. Reactivate if context changes.',
};

const QUEUES = Object.keys(QUEUE_DESCRIPTIONS).map(key => ({
  key,
  label: ({
    'my-tasks': 'My Tasks',
    'new-discoveries': 'New Discoveries',
    'needs-review': 'Needs Review',
    'high-fit': 'High-Fit Leads',
    'partner-candidates': 'Partner Candidates',
    'missing-contacts': 'Missing Contacts',
    'ready-for-outreach': 'Ready for Outreach',
    'in-outreach-silent': 'Silent In-Outreach',
    'enrichment-pending': 'Enrichment Pending',
    'stale-verdict': 'Stale Verdicts',
    'domain-stale': 'Domain Recheck Due',
    'follow-ups-due': 'Follow-Ups Due',
    'rejected': 'Rejected',
  })[key],
  icon: QUEUE_ICON_MAP[key],
  description: QUEUE_DESCRIPTIONS[key],
}));

const QUEUE_COUNT_MAP = {
  'new-discoveries': 'newDiscoveries',
  'needs-review': 'needsReview',
  'high-fit': 'highFit',
  'partner-candidates': 'partnerCandidates',
  'missing-contacts': 'missingContacts',
  'ready-for-outreach': 'readyForOutreach',
  'in-outreach-silent': 'inOutreachSilent',
  'enrichment-pending': 'enrichmentPending',
  'stale-verdict': 'staleVerdict',
  'domain-stale': 'domainStale',
  'follow-ups-due': 'followUpsDue',
  rejected: 'rejected',
};

const TASK_TYPE_LABELS = {
  FindContacts: 'Find contacts',
  ProfileLead: 'Profile lead',
  SendEmail: 'Send email',
  LinkedInOutreach: 'LinkedIn outreach',
  XingOutreach: 'Xing outreach',
  Call: 'Call',
  Meeting: 'Meeting',
  Research: 'Research',
  FollowUp: 'Follow up',
  Custom: 'Task',
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dialog = useDialog();
  const [summary, setSummary] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [tasksPanel, setTasksPanel] = useState(null); // { items, openCount, overdueCount, dueTodayCount }
  const [tasksGrid, setTasksGrid] = useState(null); // { items, count } — only when my-tasks queue selected
  const [loading, setLoading] = useState(true);
  const [tasksScope, setTasksScope] = useState('open'); // open | overdue | dueToday

  const [selectedQueue, setSelectedQueue] = useState('new-discoveries');
  const [queueData, setQueueData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);

  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);
  const [selectedDate, setSelectedDate] = useState(todayIso);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [y, m, d] = selectedDate.split('-').map(Number);
    // Widen range by ±1 day to absorb timezone offsets, then filter client-side
    const fromDt = new Date(y, m - 1, d - 1, 0, 0, 0).toISOString();
    const toDt = new Date(y, m - 1, d + 1, 23, 59, 59).toISOString();

    // allSettled — one failed call shouldn't blank out the rest
    const [sumRes, fuRes, mtRes] = await Promise.allSettled([
      getWorkspaceSummary(),
      getDueFollowUps(),
      getMeetings({ from: fromDt, to: toDt }),
    ]);

    if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
    else console.error('getWorkspaceSummary failed', sumRes.reason);

    if (fuRes.status === 'fulfilled') {
      // API may return either a plain array or a paginated wrapper { items: [...] }.
      const fuData = fuRes.value.data;
      const fuList = Array.isArray(fuData)
        ? fuData
        : Array.isArray(fuData?.items)
          ? fuData.items
          : Array.isArray(fuData?.results)
            ? fuData.results
            : [];
      setFollowUps(fuList);
    }
    else console.error('getDueFollowUps failed', fuRes.reason);

    if (mtRes.status === 'fulfilled') {
      const allMeetings = mtRes.value.data || [];
      const matchDay = (iso) => {
        const dt = new Date(iso);
        return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
      };
      setMeetings(allMeetings.filter(mtg => matchDay(mtg.startTime)));
    } else {
      console.error('getMeetings failed', mtRes.reason);
      setMeetings([]);
    }

    setLoading(false);
  }, [selectedDate]);

  // Right-panel tasks — small page (5) sized for the panel; backend computes scope counts.
  const loadTasksPanel = useCallback(async () => {
    try {
      const res = await getMyTasksPaged({ scope: tasksScope, page: 1, pageSize: 5 });
      setTasksPanel(res.data);
    } catch (e) {
      console.error('getMyTasksPaged (panel) failed', e);
      setTasksPanel({ items: [], openCount: 0, overdueCount: 0, dueTodayCount: 0 });
    }
  }, [tasksScope]);

  // Grid tasks — only when "my-tasks" queue selected; uses current pagination state.
  const loadTasksGrid = useCallback(async () => {
    if (selectedQueue !== 'my-tasks') { setTasksGrid(null); return; }
    setQueueLoading(true);
    try {
      const res = await getMyTasksPaged({ scope: 'open', page: page + 1, pageSize: rowsPerPage });
      setTasksGrid(res.data);
    } catch (e) {
      console.error('getMyTasksPaged (grid) failed', e);
    } finally {
      setQueueLoading(false);
    }
  }, [selectedQueue, page, rowsPerPage]);

  const shiftDate = (delta) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const next = new Date(y, m - 1, d + delta);
    setSelectedDate(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`);
  };

  const loadQueue = useCallback(async () => {
    if (selectedQueue === 'my-tasks') { setQueueData(null); return; }
    setQueueLoading(true);
    try {
      const res = await getWorkspaceQueue(selectedQueue, page + 1, rowsPerPage);
      setQueueData(res.data);
    } catch (e) {
      console.error('Failed to load queue', e);
    } finally {
      setQueueLoading(false);
    }
  }, [selectedQueue, page, rowsPerPage]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => { loadTasksPanel(); }, [loadTasksPanel]);
  useEffect(() => { loadTasksGrid(); }, [loadTasksGrid]);

  const handleComplete = async (id) => {
    try {
      await completeFollowUp(id, 'lead');
      loadData();
      loadQueue();
      loadTasksPanel();
    } catch { dialog.alert({ title: 'Error', message: 'Failed to complete follow-up' }); }
  };

  const handleQueueSelect = (key) => {
    setSelectedQueue(key);
    setPage(0);
    setQueueDrawerOpen(false);
  };

  const handleCompleteTask = async (task) => {
    const isOutreach = ['SendEmail', 'LinkedInOutreach', 'XingOutreach', 'Call', 'Meeting'].includes(task.type);
    try {
      await completeTask(task.id, { logActivity: isOutreach });
      // Refetch from server — paging/counts are SQL-driven, no client-side patching.
      loadTasksPanel();
      loadTasksGrid();
    } catch { dialog.alert({ title: 'Error', message: 'Failed to complete task' }); }
  };

  const visibleTasks = tasksPanel?.items || [];
  const openCount = tasksPanel?.openCount || 0;
  const overdueCount = tasksPanel?.overdueCount || 0;
  const dueTodayCount = tasksPanel?.dueTodayCount || 0;
  const visibleTasksTotal = tasksScope === 'overdue' ? overdueCount
    : tasksScope === 'dueToday' ? dueTodayCount
    : openCount;

  const counts = summary?.queueCounts || {};
  const firstName = (user?.fullName || user?.username || '').split(' ')[0];

  const isSelectedToday = selectedDate === todayIso;
  const selectedDateLabel = useMemo(() => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, [selectedDate]);
  const filteredFollowUps = useMemo(() => {
    const list = Array.isArray(followUps) ? followUps : [];
    if (isSelectedToday) return list;
    return list.filter(fu => {
      if (!fu.followUpDate) return false;
      const dt = new Date(fu.followUpDate);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      return key === selectedDate;
    });
  }, [followUps, selectedDate, isSelectedToday]);

  const columns = useMemo(() => [
    {
      key: 'companyName',
      header: 'Company',
      render: (val, row) => (
        <Box>
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
            onClick={() => navigate(`/leads/${row.id}`)}
          >
            {row.companyName}
          </Typography>
          {row.website && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {row.website}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      render: (val) => <Typography variant="body2">{val}</Typography>,
    },
    {
      key: 'icpMatchPercentage',
      header: 'ICP %',
      hideOnMobile: true,
      hideOnTablet: true,
      render: (val) => val != null ? `${val}%` : '—',
    },
    {
      key: 'azotteFitScore',
      header: 'Fit Score',
      hideOnMobile: true,
      hideOnTablet: true,
      render: (val) => val ?? '—',
    },
    {
      key: 'contactCount',
      header: 'Contacts',
      hideOnMobile: true,
      hideOnTablet: true,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_val, row) => (
        <QueueActions
          queue={selectedQueue}
          lead={row}
          navigate={navigate}
          onCompleteFollowUp={handleComplete}
        />
      ),
    },
  ], [selectedQueue, navigate, handleComplete]);

  const taskColumns = useMemo(() => [
    {
      key: 'title',
      header: 'Task',
      render: (val, row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{val}</Typography>
          <Typography variant="caption" color="text.secondary">
            {TASK_TYPE_LABELS[row.type] || row.type}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'leadCompanyName',
      header: 'Lead',
      render: (val, row) => (
        <Typography variant="body2" sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
          onClick={() => navigate(`/leads/${row.leadId}`)}>
          {val}
        </Typography>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      hideOnMobile: true,
      render: (val) => {
        if (!val) return <Typography variant="body2" color="text.disabled">—</Typography>;
        const dt = new Date(val);
        const overdue = dt < new Date();
        return (
          <Typography variant="body2" sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
            {dt.toLocaleDateString()}{overdue ? ' (overdue)' : ''}
          </Typography>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      hideOnMobile: true,
      hideOnTablet: true,
      render: (val) => <Chip label={val} size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      hideOnMobile: true,
      hideOnTablet: true,
      render: (val) => <Typography variant="caption" color="text.secondary">{new Date(val).toLocaleDateString()}</Typography>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_val, row) => (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" color="success" size="small" startIcon={<TaskAltIcon />}
            onClick={() => handleCompleteTask(row)}>
            Done
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate(`/leads/${row.leadId}`)}>
            Open
          </Button>
        </Stack>
      ),
    },
  ], [navigate, handleCompleteTask]);

  const isMyTasks = selectedQueue === 'my-tasks';
  const paginatedRows = isMyTasks
    ? (tasksGrid?.items || [])
    : (queueData?.leads || []);
  const totalCount = isMyTasks ? (tasksGrid?.count || 0) : (queueData?.count || 0);
  const activeColumns = isMyTasks ? taskColumns : columns;
  const activeLoading = isMyTasks ? queueLoading : queueLoading;

  return (
    <Box sx={{
      display: 'flex',
      height: { md: '100%' },
      minHeight: { xs: '100%' },
      overflow: { xs: 'auto', md: 'hidden' },
      flexDirection: { xs: 'column', md: 'row' },
    }}>
      {/* Main content */}
      <Box sx={{
        flex: { md: 1 },
        overflow: { md: 'auto' },
        p: { xs: 2, sm: 3, md: 4 },
        minWidth: 0,
      }}>
        {/* Greeting */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography
            sx={{
              color: '#1a1a2e',
              fontWeight: 500,
              lineHeight: 1.2,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            }}
          >
            {getGreeting()}, {firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Quick Actions</Typography>
          <Grid container spacing={{ xs: 1, sm: 1.5 }}>
            {QUICK_ACTIONS.map((a) => (
              <Grid
                key={a.path}
                size={{ xs: 6, sm: 4, md: 'grow' }}
                sx={a.hideOnMobile ? { display: { xs: 'none', sm: 'block' } } : undefined}
              >
                <Card variant="outlined" sx={{ height: '100%', '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' }, transition: 'all 0.15s' }}>
                  <CardActionArea onClick={() => navigate(a.path)} sx={{ p: { xs: 1.25, sm: 2 }, height: '100%' }}>
                    <Box sx={{ color: 'primary.main', mb: { xs: 0.5, sm: 1 } }}>{a.icon}</Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{a.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>{a.desc}</Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Work Queues + Queue Table */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}>
          {/* Sidebar (desktop only) */}
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            width: 260,
            minWidth: 200,
            maxWidth: 260,
            borderRight: 1,
            borderColor: 'divider',
            p: 2,
            overflowY: 'auto',
          }}>
            <Typography
              variant="overline"
              sx={{ display: 'block', mb: 1, color: 'text.secondary', letterSpacing: 1 }}
            >
              Work Queues
            </Typography>
            <QueueList
              queues={QUEUES}
              selectedQueue={selectedQueue}
              onSelect={handleQueueSelect}
              myTasksCount={openCount}
              counts={counts}
            />
          </Box>

          {/* Hamburger Drawer (mobile/tablet) */}
          <Drawer
            anchor="left"
            open={queueDrawerOpen}
            onClose={() => setQueueDrawerOpen(false)}
            sx={{ display: { xs: 'block', md: 'none' } }}
            PaperProps={{ sx: { width: 280, p: 2 } }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                Work Queues
              </Typography>
              <IconButton size="small" onClick={() => setQueueDrawerOpen(false)} aria-label="close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            <QueueList
              queues={QUEUES}
              selectedQueue={selectedQueue}
              onSelect={handleQueueSelect}
              myTasksCount={openCount}
              counts={counts}
            />
          </Drawer>

          <Box sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 } }}>
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                <IconButton
                  size="small"
                  onClick={() => setQueueDrawerOpen(true)}
                  aria-label="open work queues"
                  sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 0.5 }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
                {QUEUE_ICON_MAP[selectedQueue]}
                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} noWrap>
                  {QUEUES.find(q => q.key === selectedQueue)?.label}
                </Typography>
                {(isMyTasks ? totalCount > 0 : queueData) && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                    ({isMyTasks ? totalCount : queueData.count})
                  </Typography>
                )}
              </Stack>
            </Stack>

            {QUEUE_DESCRIPTIONS[selectedQueue] && (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', mb: 1.5, ml: 0.5 }}
              >
                {QUEUE_DESCRIPTIONS[selectedQueue]}
              </Typography>
            )}

            <AzotteGrid
              columns={activeColumns}
              rows={paginatedRows}
              totalCount={totalCount}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              loading={activeLoading}
              onDoubleClick={(row) => navigate(`/leads/${isMyTasks ? row.leadId : row.id}`)}
              actionColumnKey="actions"
            />
          </Box>
        </Box>
      </Box>

      {/* Right panel */}
      <Box sx={{
        width: { xs: '100%', md: 320 },
        minWidth: { md: 320 },
        flexShrink: 0,
        borderLeft: { md: '1px solid' },
        borderTop: { xs: '1px solid', md: 'none' },
        borderColor: 'divider',
        bgcolor: 'grey.50',
        overflow: { md: 'auto' },
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2, md: 3 },
      }}>
        {/* Date picker */}
        <Box>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, mb: 1 }}>
            <IconButton size="small" onClick={() => shiftDate(-1)} title="Previous day">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <TextField
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ flex: 1, '& input': { fontSize: '0.85rem', py: 0.75 } }}
            />
            <IconButton size="small" onClick={() => shiftDate(1)} title="Next day">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setSelectedDate(todayIso)}
              title="Today"
              disabled={isSelectedToday}
            >
              <TodayIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Meetings */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            📅 {isSelectedToday ? "Today's Meetings" : `Meetings · ${selectedDateLabel}`}
          </Typography>
          {loading ? <CircularProgress size={20} /> :
            meetings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {isSelectedToday ? 'No meetings today' : 'No meetings on this day'}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {meetings.map(m => (
                  <Paper key={m.id} onClick={() => navigate('/calendar')} sx={{ p: 1.5, cursor: 'pointer', '&:hover': { boxShadow: 1 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(m.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' – '}
                      {new Date(m.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                    {m.location && <Typography variant="caption" display="block" color="text.disabled">📍 {m.location}</Typography>}
                  </Paper>
                ))}
              </Stack>
            )}
        </Box>

        {/* Due Follow-Ups */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            ⏰ {isSelectedToday ? 'Due Follow-Ups' : `Follow-Ups · ${selectedDateLabel}`}
          </Typography>
          {loading ? <CircularProgress size={20} /> :
            filteredFollowUps.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {isSelectedToday ? 'No follow-ups due' : 'No follow-ups on this day'}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {filteredFollowUps.slice(0, 8).map(fu => (
                  <Paper key={fu.id || fu.leadId} sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={() => fu.leadId && navigate(`/leads/${fu.leadId}`)}>
                        {fu.leadName || fu.companyName || 'Follow-up'}
                      </Typography>
                      {fu.followUpAction && <Typography variant="caption" color="text.secondary">{fu.followUpAction}</Typography>}
                      {fu.followUpDate && (
                        <Typography variant="caption" display="block" sx={{ color: new Date(fu.followUpDate) < new Date() ? 'error.main' : 'text.secondary' }}>
                          {new Date(fu.followUpDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" variant="contained" color="success" onClick={() => handleComplete(fu.leadId)}
                      startIcon={<CheckCircleIcon />} sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      Done
                    </Button>
                  </Paper>
                ))}
                {filteredFollowUps.length > 8 && (
                  <Button size="small" onClick={() => handleQueueSelect('follow-ups-due')} sx={{ alignSelf: 'center' }}>
                    View all {filteredFollowUps.length} follow-ups →
                  </Button>
                )}
              </Stack>
            )}
        </Box>

        {/* My Tasks */}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AssignmentIcon sx={{ fontSize: 16 }} /> My Tasks
              {openCount > 0 && <Chip label={openCount} size="small" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />}
              {overdueCount > 0 && <Chip label={`${overdueCount} overdue`} size="small" color="error" sx={{ height: 18, fontSize: '0.7rem', ml: 0.5 }} />}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
            {[
              { key: 'open', label: 'Open' },
              { key: 'overdue', label: 'Overdue' },
              { key: 'dueToday', label: 'Due today' },
            ].map(t => (
              <Chip key={t.key} label={t.label} size="small"
                color={tasksScope === t.key ? 'primary' : 'default'}
                variant={tasksScope === t.key ? 'filled' : 'outlined'}
                onClick={() => setTasksScope(t.key)}
                sx={{ height: 22, fontSize: '0.7rem', cursor: 'pointer' }} />
            ))}
          </Stack>
          {tasksPanel === null ? <CircularProgress size={20} /> : visibleTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {tasksScope === 'overdue' ? 'No overdue tasks' : tasksScope === 'dueToday' ? 'Nothing due today' : 'No open tasks'}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleTasks.map(t => {
                const due = t.dueDate ? new Date(t.dueDate) : null;
                const overdue = due && due < new Date();
                return (
                  <Paper key={t.id} sx={{ p: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                        onClick={() => navigate(`/leads/${t.leadId}`)}>
                        {t.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t.leadCompanyName}
                      </Typography>
                      {due && (
                        <Typography variant="caption" display="block"
                          sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
                          Due {due.toLocaleDateString()}{overdue ? ' · overdue' : ''}
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" variant="contained" color="success"
                      onClick={() => handleCompleteTask(t)}
                      startIcon={<TaskAltIcon />}
                      sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                      Done
                    </Button>
                  </Paper>
                );
              })}
              {visibleTasksTotal > visibleTasks.length && (
                <Button size="small" onClick={() => handleQueueSelect('my-tasks')} sx={{ alignSelf: 'center' }}>
                  View all {visibleTasksTotal} tasks →
                </Button>
              )}
            </Stack>
          )}
        </Box>

        {/* Today's Focus */}
        <Paper sx={{ p: 2, bgcolor: '#f0edff', border: 'none' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#5a4fcf' }}>💡 Today's Focus</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.8 } }}>
            <li>Review new discoveries</li>
            <li>Follow up on overdue leads</li>
            <li>Engage high-fit leads</li>
            <li>Check campaign responses</li>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function QueueList({ queues, selectedQueue, onSelect, myTasksCount, counts }) {
  return (
    <List dense disablePadding>
      {queues.map(q => {
        const c = q.key === 'my-tasks' ? myTasksCount : (counts[QUEUE_COUNT_MAP[q.key]] || 0);
        const active = selectedQueue === q.key;
        return (
          <Tooltip key={q.key} title={q.description || ''} placement="right" arrow disableInteractive>
          <ListItemButton
            selected={active}
            onClick={() => onSelect(q.key)}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {q.icon}
            </ListItemIcon>
            <ListItemText
              primary={q.label}
              primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 600 : 400 }}
            />
            {c > 0 && (
              <Chip
                label={c}
                size="small"
                color={active ? 'primary' : 'default'}
                sx={{ height: 22, fontSize: 12, fontWeight: 600 }}
              />
            )}
          </ListItemButton>
          </Tooltip>
        );
      })}
    </List>
  );
}

function QueueActions({ queue, lead, navigate, onCompleteFollowUp }) {
  const view = () => navigate(`/leads/${lead.id}`);

  switch (queue) {
    case 'new-discoveries':
      return <Button variant="contained" size="small" onClick={view}>Review</Button>;
    case 'needs-review':
      return <Button variant="contained" size="small" onClick={view}>Review Fields</Button>;
    case 'high-fit':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={() => navigate('/engage')}>Add to Campaign</Button>
          <Button variant="outlined" size="small" onClick={view}>View</Button>
        </Stack>
      );
    case 'missing-contacts':
      return <Button variant="contained" size="small" onClick={view}>Add Contact</Button>;
    case 'ready-for-outreach':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={() => navigate('/engage')}>Create Campaign</Button>
          <Button variant="outlined" size="small" onClick={view}>View</Button>
        </Stack>
      );
    case 'follow-ups-due':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={view}>View</Button>
          <Button variant="outlined" size="small" onClick={() => onCompleteFollowUp(lead.id)}>Complete</Button>
        </Stack>
      );
    case 'rejected':
      return <Button variant="outlined" size="small" onClick={view}>View</Button>;
    case 'partner-candidates':
      return <Button variant="contained" size="small" onClick={view}>View Partner</Button>;
    case 'in-outreach-silent':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={view}>Nudge</Button>
          <Button variant="outlined" size="small" onClick={view}>View</Button>
        </Stack>
      );
    case 'enrichment-pending':
      return <Button variant="outlined" size="small" onClick={view}>View</Button>;
    case 'stale-verdict':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={view}>Re-enrich</Button>
          <Button variant="outlined" size="small" onClick={view}>View</Button>
        </Stack>
      );
    case 'domain-stale':
      return (
        <Stack direction="row" spacing={0.5}>
          <Button variant="contained" size="small" onClick={view}>Recheck</Button>
          <Button variant="outlined" size="small" onClick={view}>View</Button>
        </Stack>
      );
    default:
      return <Button variant="outlined" size="small" onClick={view}>View</Button>;
  }
}
